// ** Redux Imports
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"

// ** Axios Imports
import {
  getWebsiteIconApi,
  getWebsiteIconByIdApi,
  addOrUpdateWebsiteIconApi
} from "../../../../services/Web/webIcon.service"

import { toastError, toastSuccess } from "../../../../utility/toastutill"

// ** Async Thunks
export const fetchWebsiteIcon = createAsyncThunk(
  "websiteIcon/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getWebsiteIconApi()
      return response.data
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to fetch website icon data"
      toastError(errorMessage)
      return rejectWithValue({
        message: errorMessage,
        status: error.response?.status
      })
    }
  }
)

export const fetchWebsiteIconById = createAsyncThunk(
  "websiteIcon/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await getWebsiteIconByIdApi(id)
      return response.data
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to fetch website icon data"
      toastError(errorMessage)
      return rejectWithValue({
        message: errorMessage,
        status: error.response?.status
      })
    }
  }
)

export const createOrUpdateWebsiteIcon = createAsyncThunk(
  "websiteIcon/createOrUpdate",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await addOrUpdateWebsiteIconApi(formData)
      toastSuccess("Website icon saved successfully")
      return response.data
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to save website icon data"
      toastError(errorMessage)
      return rejectWithValue({
        message: errorMessage,
        status: error.response?.status
      })
    }
  }
)

// ** Initial State
const initialState = {
  data: null,
  selectedWebsiteIcon: null,
  loading: false,
  error: null,
  success: false,
  total: 0,
  lastUpdated: null
}

// ** Slice
export const websiteIconSlice = createSlice({
  name: "websiteIcon",
  initialState,
  reducers: {
    clearSelectedWebsiteIcon: (state) => {
      state.selectedWebsiteIcon = null
    },
    clearError: (state) => {
      state.error = null
    },
    clearSuccess: (state) => {
      state.success = false
    },
    resetWebsiteIconState: (state) => {
      state.data = null
      state.selectedWebsiteIcon = null
      state.loading = false
      state.error = null
      state.success = false
      state.total = 0
      state.lastUpdated = null
    },
    updateFormData: (state, action) => {
      const updatedData = action.payload

      if (state.selectedWebsiteIcon) {
        state.selectedWebsiteIcon = {
          ...state.selectedWebsiteIcon,
          ...updatedData
        }
      }

      if (state.data) {
        state.data = {
          ...state.data,
          ...updatedData
        }
      }

      state.lastUpdated = new Date().toISOString()
    },
    setLoading: (state, action) => {
      state.loading = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch website icon data
      .addCase(fetchWebsiteIcon.pending, (state) => {
        state.loading = true
        state.error = null
        state.success = false
      })
      .addCase(fetchWebsiteIcon.fulfilled, (state, action) => {
        state.loading = false
        const responseData = action.payload.data || action.payload

        state.data = responseData
        state.selectedWebsiteIcon = responseData
        state.total = 1
        state.lastUpdated = new Date().toISOString()
        state.error = null
        state.success = true
      })
      .addCase(fetchWebsiteIcon.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
        state.lastUpdated = new Date().toISOString()
      })
      // Fetch website icon by ID
      .addCase(fetchWebsiteIconById.pending, (state) => {
        state.loading = true
        state.error = null
        state.success = false
      })
      .addCase(fetchWebsiteIconById.fulfilled, (state, action) => {
        state.loading = false
        const responseData = action.payload.data || action.payload
        state.selectedWebsiteIcon = responseData

        if (state.data && state.data._id === responseData._id) {
          state.data = responseData
        }
        state.lastUpdated = new Date().toISOString()
        state.error = null
        state.success = true
      })
      .addCase(fetchWebsiteIconById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
        state.lastUpdated = new Date().toISOString()
      })
      // Create or update website icon
      .addCase(createOrUpdateWebsiteIcon.pending, (state) => {
        state.loading = true
        state.error = null
        state.success = false
      })
      .addCase(createOrUpdateWebsiteIcon.fulfilled, (state, action) => {
        state.loading = false
        const updatedData = action.payload.data || action.payload

        state.data = updatedData
        state.selectedWebsiteIcon = updatedData
        state.total = 1
        state.lastUpdated = new Date().toISOString()
        state.error = null
        state.success = true
      })
      .addCase(createOrUpdateWebsiteIcon.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
        state.lastUpdated = new Date().toISOString()
      })
  }
})

// ** Actions
export const {
  clearSelectedWebsiteIcon,
  clearError,
  clearSuccess,
  resetWebsiteIconState,
  updateFormData,
  setLoading
} = websiteIconSlice.actions

// ** Selectors
export const selectWebsiteIcon = (state) => state.websiteIcon.data
export const selectSelectedWebsiteIcon = (state) => state.websiteIcon.selectedWebsiteIcon
export const selectWebsiteIconLoading = (state) => state.websiteIcon.loading
export const selectWebsiteIconError = (state) => state.websiteIcon.error
export const selectWebsiteIconSuccess = (state) => state.websiteIcon.success
export const selectWebsiteIconLastUpdated = (state) => state.websiteIcon.lastUpdated

// ** Reducer
export default websiteIconSlice.reducer
